import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { handleApiBadges } from "@/lib/badges";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Mail, Eye, Bell, Sparkles, Loader2, Trash2 } from "lucide-react";

export default function Students() {
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [notifyAllLoading, setNotifyAllLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();

  const fetchStudents = () => api.get('/students').then(r => setStudents(r.data)).catch(() => {});

  useEffect(() => { fetchStudents(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/students', form);
      const data = res.data;
      handleApiBadges(res, toast);
      toast.success(data.temp_password ? `Élève créé ! Mot de passe temporaire : ${data.temp_password}` : "Élève ajouté !");
      setOpen(false);
      setForm({ name: "", email: "" });
      fetchStudents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (studentId) => {
    setDeletingId(studentId);
    try {
      await api.delete(`/students/${studentId}`);
      setStudents(prev => prev.filter(s => s.user_id !== studentId));
      toast.success("Élève supprimé de votre liste");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeletingId(null);
    }
  };

  const handleNotifyAll = async () => {
    if (!students.length) return;
    if (!window.confirm(`Envoyer des notifications IA à ${students.length} élève${students.length > 1 ? 's' : ''} ?`)) return;
    setNotifyAllLoading(true);
    let totalCreated = 0;
    let errors = 0;
    for (const s of students) {
      try {
        const { data } = await api.post('/ai/smart-notifications', { student_id: s.user_id });
        totalCreated += data.created;
      } catch {
        errors++;
      }
    }
    setNotifyAllLoading(false);
    if (errors > 0) {
      toast.warning(`${totalCreated} notifications envoyées (${errors} erreur${errors > 1 ? 's' : ''})`);
    } else {
      toast.success(`${totalCreated} notifications envoyées à ${students.length} élève${students.length > 1 ? 's' : ''}`);
    }
  };

  const filtered = students.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) || s.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="students-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Élèves</h1>
          <p className="text-slate-500 mt-1">{students.length} élève{students.length > 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          {students.length > 0 && (
            <Button
              onClick={handleNotifyAll}
              disabled={notifyAllLoading}
              variant="outline"
              className="gap-2 text-orange-600 border-orange-200 hover:bg-orange-50 rounded-full"
            >
              {notifyAllLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Bell className="w-4 h-4" /><Sparkles className="w-3 h-3 -ml-1" /></>}
              {notifyAllLoading ? "Envoi en cours..." : "Notifier tous IA"}
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full" data-testid="add-student-btn">
                <Plus className="w-4 h-4 mr-2" /> Ajouter un élève
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Ajouter un élève</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4 mt-4">
              <div>
                <Label>Nom</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="mt-1.5 h-11" data-testid="student-name-input" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required className="mt-1.5 h-11" data-testid="student-email-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-11" data-testid="submit-student-btn">
                {loading ? "Ajout..." : "Ajouter"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="relative mt-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un élève..." className="pl-10 h-11 bg-white" data-testid="search-students-input" />
      </div>

      <div className="grid gap-3 mt-6">
        {filtered.length === 0 ? (
          <p className="text-center text-slate-400 py-12">Aucun élève trouvé</p>
        ) : filtered.map(s => (
          <Card key={s.user_id} className="border-slate-100 hover:border-blue-200 hover:shadow-sm transition-all cursor-pointer" onClick={() => navigate(`/coach/students/${s.user_id}`)} data-testid={`student-card-${s.user_id}`}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-600 flex-shrink-0">
                {s.name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900">{s.name}</p>
                <p className="text-sm text-slate-400 flex items-center gap-1"><Mail className="w-3 h-3" /> {s.email}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(`/coach/students/${s.user_id}`)}
                  data-testid={`view-student-${s.user_id}`}
                >
                  <Eye className="w-4 h-4 text-slate-400" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      disabled={deletingId === s.user_id}
                      data-testid={`delete-student-${s.user_id}`}
                    >
                      {deletingId === s.user_id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Trash2 className="w-4 h-4" />}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Supprimer {s.name} ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Cet élève sera retiré de votre liste. Son compte sera conservé mais il ne sera plus associé à votre coaching.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete(s.user_id)}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        Supprimer
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
